import { BuilderActionPartial } from "@src/features/libraryItem/contexts/BuilderActionContext";
import { BuilderActionFragment } from "@src/features/libraryItem/hooks/usePollServerForBuilderActions.generated";
import { BuilderActionInput } from "@src/types.generated";
import shouldActionsBeDeduped from "@src/features/libraryItem/module/utils/shouldActionsBeDeduped";
import { useApplyBuilderActionsMutation } from "@src/features/libraryItem/course/build/CourseBuildContent.generated";
import { ApplyBuilderActionsMutation } from "@src/features/libraryItem/course/build/CourseBuildContent.generated";
import { GraphqlOperations } from "@src/types.generated";
import {
  createBuilderActionsSyncStore,
  useBuilderActionsFlushDriver,
  useSyncMutationHandlers,
} from "@src/features/builders/shared/autosave/builderActionsSync";

// Queue item — BuilderActionPartial carries the payload + the
// client-side fields needed by the dedupe predicate (timestamp) and
// optimistic apply flow (addedModuleContentMembership). The queue
// adds uuid + clientId.
type SyncQueueBuilderAction = BuilderActionPartial &
  Pick<BuilderActionFragment, "uuid" | "clientId">;

export const useSyncBuilderActionsStore =
  createBuilderActionsSyncStore<SyncQueueBuilderAction>(shouldActionsBeDeduped);

const toBuilderActionInput = (
  item: SyncQueueBuilderAction,
): BuilderActionInput => ({
  uuid: item.uuid,
  clientId: item.clientId,
  courseBuilderAction: item.payload.courseBuilderAction,
  moduleBuilderAction: item.payload.moduleBuilderAction,
  skillBuilderAction: item.payload.skillBuilderAction,
  trainingResourceBuilderAction: item.payload.trainingResourceBuilderAction,
});

/**
 * Membership uuids of queued content adds the server applied without
 * creating a membership. It skips an add whose content the module
 * already holds, so the optimistic row is a duplicate to drop.
 */
export const skippedMembershipAddUuids = (
  queue: ReadonlyArray<SyncQueueBuilderAction>,
  applied: ReadonlyArray<{
    uuid: string;
    addedModuleContentMembership?: { uuid: string } | null;
  }>,
): string[] => {
  const skippedActionUuids = new Set(
    applied.filter((a) => !a.addedModuleContentMembership).map((a) => a.uuid),
  );
  return queue.flatMap((item) => {
    const add =
      item.payload.moduleBuilderAction?.addModuleContentMembershipAction;
    if (!add || add.sectionBreak || !skippedActionUuids.has(item.uuid))
      return [];
    return [add.moduleContentMembershipUuid];
  });
};

// Drives the flush lifecycle. Mount once at a level that outlives
// every component that can enqueue (BuilderActionContextProvider).
const useSyncBuilderActionsToServer = (
  onSkippedMembershipAdds?: (membershipUuids: string[]) => void,
): void => {
  const handlers = useSyncMutationHandlers(useSyncBuilderActionsStore);
  const [applyBuilderActionsMutation, { loading }] =
    useApplyBuilderActionsMutation({
      refetchQueries: [
        GraphqlOperations.Query.SaveModuleDialog,
        GraphqlOperations.Query.CourseVersion,
      ],
      ...handlers,
      onCompleted: (data: ApplyBuilderActionsMutation) => {
        const r = data.applyBuilderActions;
        if (r.success && onSkippedMembershipAdds) {
          const skipped = skippedMembershipAddUuids(
            useSyncBuilderActionsStore.getState().queue,
            r.appliedBuilderActions ?? [],
          );
          if (skipped.length) onSkippedMembershipAdds(skipped);
        }
        handlers.onCompleted(data);
      },
    });

  useBuilderActionsFlushDriver({
    store: useSyncBuilderActionsStore,
    mutate: applyBuilderActionsMutation,
    loading,
    transform: toBuilderActionInput,
  });
};

export default useSyncBuilderActionsToServer;
